import type {
	InformationGatheringDestination,
	InformationGatheringPatientIdentity,
	InformationGatheringRole,
	SexAssignedAtBirth,
} from './information-gathering.model';

export interface InformationGatheringEntry {
	role: InformationGatheringRole;
	patientIdentity: InformationGatheringPatientIdentity;
	preselectedSexAssignedAtBirth: SexAssignedAtBirth | null;
	destination: InformationGatheringDestination;
}

export interface CompletedInformationGathering {
	submissionId: string;
}

interface InformationGatheringEntryState {
	informationGathering: {
		kind: 'entry';
		key: string;
	};
}

export interface CompletedInformationGatheringState {
	informationGathering: {
		kind: 'completed';
		key: string;
	};
}

const informationGatheringEntries = new Map<string, InformationGatheringEntry>();

export function createInformationGatheringEntryState(entry: InformationGatheringEntry): InformationGatheringEntryState {
	const key = crypto.randomUUID();
	informationGatheringEntries.set(key, entry);

	return {
		informationGathering: {
			kind: 'entry',
			key,
		},
	};
}

export function createCompletedInformationGatheringState(value: CompletedInformationGathering): CompletedInformationGatheringState {
	return {
		informationGathering: {
			kind: 'completed',
			key: value.submissionId,
		},
	};
}

export function getInformationGatheringEntry(state: unknown): InformationGatheringEntry | null {
	const informationGathering = getInformationGatheringState(state);
	if (informationGathering?.kind !== 'entry') {
		return null;
	}

	return informationGatheringEntries.get(informationGathering.key) ?? null;
}

export function getCompletedInformationGathering(state: unknown): CompletedInformationGathering | null {
	const informationGathering = getInformationGatheringState(state);
	if (informationGathering?.kind !== 'completed') {
		return null;
	}

	return { submissionId: informationGathering.key };
}

export function releaseInformationGatheringState(state: unknown): void {
	const informationGathering = getInformationGatheringState(state);
	if (informationGathering?.kind === 'entry') {
		informationGatheringEntries.delete(informationGathering.key);
	}
}

function getInformationGatheringState(state: unknown) {
	if (typeof state !== 'object' || state === null || !('informationGathering' in state)) {
		return null;
	}

	const informationGathering = state.informationGathering;
	if (
		typeof informationGathering !== 'object' ||
		informationGathering === null ||
		!('kind' in informationGathering) ||
		!('key' in informationGathering) ||
		typeof informationGathering.kind !== 'string' ||
		typeof informationGathering.key !== 'string'
	) {
		return null;
	}

	if (informationGathering.kind !== 'entry' && informationGathering.kind !== 'completed') {
		return null;
	}

	const { kind, key } = informationGathering;

	if (kind !== 'entry' && kind !== 'completed') {
		return null;
	}

	return { kind, key };
}
